/** The phone menu's swipe-to-close, as pure arithmetic over a finger's travel. */
export const SWIPE = {
    SLOP_PX: 10,
    CLOSE_FRACTION: 0.3,
    FLICK_PX_PER_MS: 0.45,
    FLICK_MIN_PX: 24,
    FLICK_WINDOW_MS: 100,
    CLOSE_MS: 160,
    SPRING_MS: 180,
} as const;

export type SwipeDecision = "pending" | "claimed" | "rejected";

export interface SwipeSample {
    /** The event's timestamp, in ms. */
    t: number;
    x: number;
}

/** Leftward only, so the gesture never competes with iOS's edge Back, which goes rightward. */
export function decideSwipe(dx: number, dy: number): SwipeDecision {
    if (Math.hypot(dx, dy) < SWIPE.SLOP_PX) return "pending";
    return dx < 0 && Math.abs(dx) > Math.abs(dy) ? "claimed" : "rejected";
}

/** Horizontal px/ms between the oldest and newest samples of the last `FLICK_WINDOW_MS`. */
export function velocityOf(samples: readonly SwipeSample[], now: number): number {
    const recent = samples.filter((sample) => sample.t >= now - SWIPE.FLICK_WINDOW_MS);
    if (recent.length < 2) return 0;
    const oldest = recent[0];
    const newest = recent[recent.length - 1];
    const elapsed = newest.t - oldest.t;
    return elapsed > 0 ? (newest.x - oldest.x) / elapsed : 0;
}

export function shouldDismiss({ dx, width, velocity }: { dx: number; width: number; velocity: number }): boolean {
    const travelled = -dx;
    if (width > 0 && travelled >= width * SWIPE.CLOSE_FRACTION) return true;
    return velocity <= -SWIPE.FLICK_PX_PER_MS && travelled >= SWIPE.FLICK_MIN_PX;
}
